/**
 * A data-provider credit that has to stay painted on the map while that provider's data is drawn.
 *
 * Most of our maps need nothing here: they keep Mapbox GL JS's default `AttributionControl`, which
 * already renders the `attribution` string each loaded source declares — the served Lidar style
 * declares one on its `mapterhorn-dem` source, and the National+ / OS (GB) styles declare OS's Crown
 * copyright line on their OS sources. This exists for the maps that switch that control off
 * (Explore, the hike and recording embeds, compare maps, the elevation preview, the map style
 * pages), where those credits are met by nothing else on screen.
 *
 * The credit is derived from the *loaded style's own sources*, the same input GL JS's control reads,
 * rather than from the selected style's name. Base styles get swapped through several different code
 * paths on web — the styles dialog, the Vuex module, print mode, and two components that clone
 * `mainMap.getStyle()` — so reading the style that actually ended up on the map is the only check
 * that can't be bypassed by a path nobody remembered to update.
 */
export interface MapDataCredit {
  label: string;
  attributionUrl: string;
}

/**
 * Matches the wording Mapterhorn's own tile source declares in the served style JSON, so the pill
 * and the standard attribution strip read identically. Kept in step with iOS's and Android's
 * `MapDataCredit.mapterhorn`.
 */
export const MAPTERHORN_CREDIT: MapDataCredit = {
  label: '© Mapterhorn',
  attributionUrl: 'https://mapterhorn.com/attribution',
};

/** Substring we look for in a source's `attribution` HTML. */
const MAPTERHORN_MARKER = 'mapterhorn';

/**
 * The one field we care about from a style spec's `sources` map. The index signature is what lets a
 * real source spec — `type`, `tiles`, `url`, `encoding` and the rest — be passed straight in, rather
 * than tripping TypeScript's weak-type check for having nothing in common with a lone optional.
 */
export interface StyleSourceLike {
  attribution?: string;
  [key: string]: unknown;
}

/**
 * The credits owed by a loaded style, keyed off what its sources declare.
 *
 * Mapbox and OpenStreetMap are deliberately not returned: on the surfaces that keep the default
 * control they are already credited there, and on the surfaces that don't, their own branding is a
 * separate decision from Mapterhorn's licence.
 *
 * A provider is credited whenever a source declares its attribution, without checking whether a
 * layer currently draws from it. Over-crediting a style that ships the source but never paints it
 * is harmless; under-crediting one is a licence problem.
 */
export function mapDataCreditsForSources(
  sources: Record<string, StyleSourceLike | null | undefined> | null | undefined,
): MapDataCredit[] {
  if (!sources) return [];

  const attributions = Object.values(sources).map((source) => source?.attribution ?? '');
  const credits: MapDataCredit[] = [];

  const os = attributions.find((attribution) => OS_MARKER.test(attribution));
  if (os) credits.push(osCreditFromAttribution(os));

  if (attributions.some((attribution) => attribution.toLowerCase().includes(MAPTERHORN_MARKER))) {
    credits.push(MAPTERHORN_CREDIT);
  }

  return credits;
}

/**
 * Ordnance Survey's mandatory "© Crown copyright and database rights <year> OS <licence>" line,
 * which the OS (GB) sources of the served styles declare as a link to OS's homepage. The label is
 * read from that attribution rather than hard-coded, so the year and licence number shown here are
 * always the ones the style (and so the default attribution strip and the apps) carry.
 */
const OS_MARKER = /crown copyright/i;

export const OS_HOMEPAGE_URL = 'https://www.ordnancesurvey.co.uk/';

export function osCreditFromAttribution(attribution: string): MapDataCredit {
  const href = attribution.match(/href\s*=\s*["']([^"']+)["']/i)?.[1];
  const label = attribution
    .replace(/<[^>]*>/g, '')
    .replace(/&copy;/gi, '©')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
  return { label, attributionUrl: href && /^https?:\/\//i.test(href) ? href : OS_HOMEPAGE_URL };
}
